/**
 * RiskProfilesView - Driver Risk Profile Rankings
 *
 * Displays driver behavior scores in a league table format
 * Similar to Quartix rankings/league tables
 */

import React, { useState, useEffect } from 'react';
import { Trophy, RefreshCw, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { driverAPI, riskProfileAPI } from '../../api.js';
import DriverLeagueTable from './tables/DriverLeagueTable.jsx';

export default function RiskProfilesView() {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState([]);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch drivers and risk profiles in parallel
      const [driversRes, rankingsRes, statsRes] = await Promise.all([
        driverAPI.list({ _limit: 100 }),
        riskProfileAPI.getRankings(),
        riskProfileAPI.getStats()
      ]);

      if (driversRes?.ok) {
        setDrivers(driversRes.data || []);
      }

      if (rankingsRes?.ok) {
        setRiskProfiles(rankingsRes.data || []);
      }

      if (statsRes?.ok) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('Error loading risk profiles:', err);
      setError(err.message || t('common.states.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDriver = (driverId) => {
    // TODO-006: Open driver detail modal (see docs/TODO_ISSUES.md)
    console.log('View driver:', driverId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-amber-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('nav.risk-profiles')}</h1>
            <p className="text-sm text-gray-500">{t('drivers.risk.leaderboard.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.actions.refresh')}
        </button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard
            label={t('drivers.risk.leaderboard.averageScore')}
            value={stats.average_score?.toFixed(0) || '-'}
            color="blue"
          />
          <StatCard
            label={t('drivers.risk.leaderboard.bestScore')}
            value={stats.best_score?.toFixed(0) || '-'}
            color="green"
          />
          <StatCard
            label={t('drivers.risk.leaderboard.worstScore')}
            value={stats.worst_score?.toFixed(0) || '-'}
            color="red"
          />
          <StatCard
            label={t('drivers.risk.leaderboard.driversCount')}
            value={stats.total_drivers || drivers.length}
            color="slate"
          />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* League Table */}
      {!loading && !error && (
        <DriverLeagueTable
          drivers={drivers}
          riskProfiles={riskProfiles}
          onViewDriver={handleViewDriver}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div className={`p-4 rounded-lg border ${colors[color] || colors.slate}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm opacity-75">{label}</div>
    </div>
  );
}
